import { useState } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { transformMultiselectToReadable } from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import * as Form from 'components/form';
import { ValidationTextMap } from 'components/form/validations';
import { CrmMemberComboboxItem } from 'components/tasks/CrmMemberComboboxItem';
import * as TaskAssignee from 'components/tasks/TaskAssignee';
import { useCrmMembers } from 'components/tasks/utils/useCrmMembers';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { identity } from 'lodash';
import { CrmMember } from 'types/domain';

interface UnassignedCrmMember extends Omit<CrmMember, 'id'> {
    id: string;
}
const noAssigneeMember: UnassignedCrmMember = {
    id: 'unassigned',
    first_name: 'No',
    last_name: 'assignee',
    email: '',
    phone: '',
    uid: '',
};

interface AssignedToFilterFormValues extends FilterFormValue<string[]> {}

export const filterKey: ServerKey = 'assigned_to';
export const Label: LabelFilterComponent = () => 'Assignee';
export const Icon: FilterIcon = faUser;

export const Filter: ContentFilterComponent = () => {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const currentFilter = useCurrentFilter<(string | number)[]>();

    const [_, params] = useIsPending(
        {
            query: debouncedSearch || undefined,
            limit: 9999,
        },
        500,
    );

    const { isPending, data: members } = useCrmMembers(params);
    const selectedMembers = [...members, noAssigneeMember].filter(m =>
        currentFilter?.value.includes(m.id),
    );

    const filterContext = useFilter<(string | number)[], string[]>({
        formValueToFilter: item => item.map(c => (c === 'unassigned' ? c : parseInt(c))),
    });

    const methods = Form.useForm<AssignedToFilterFormValues>({
        defaultValues: {
            value: selectedMembers.map(c => c.id.toString()),
        },
    });
    const { control, path } = methods;

    return (
        <LoadingOverlay size={'sm'} isLoading={isPending} delay={2000}>
            <FilterFormContext.Provider value={filterContext}>
                <Form.Provider {...methods}>
                    <FilterForm>
                        <FilterContainer>
                            <Form.Container>
                                <Form.Label className="sr-only" name={path('value')}>
                                    Assignees
                                </Form.Label>
                                <Form.Combobox
                                    name="value"
                                    control={control}
                                    rules={{ required: ValidationTextMap.Required }}
                                >
                                    <Combobox.Content>
                                        <Combobox.Search
                                            placeholder="Search"
                                            value={search}
                                            onChangeValue={setSearch}
                                            isLoading={isPending}
                                        />
                                        <FilterComboboxItems>
                                            <Combobox.Item
                                                key={noAssigneeMember.id}
                                                value={noAssigneeMember.id}
                                            >
                                                <Combobox.CheckboxIndicator />
                                                <Combobox.ItemText>
                                                    <TaskAssignee.Content assignee={null} />
                                                </Combobox.ItemText>
                                            </Combobox.Item>
                                            {members.map(item => {
                                                return (
                                                    <CrmMemberComboboxItem
                                                        member={item}
                                                        key={item.id}
                                                    />
                                                );
                                            })}
                                        </FilterComboboxItems>
                                    </Combobox.Content>
                                </Form.Combobox>
                                <Form.Error name={path('value')} />
                            </Form.Container>
                            <FilterSubmit />
                        </FilterContainer>
                    </FilterForm>
                </Form.Provider>
            </FilterFormContext.Provider>
        </LoadingOverlay>
    );
};
export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<(string | number)[]>();
    const { isPending, data: members } = useCrmMembers();
    const selectedMembers = [...members, noAssigneeMember].filter(m =>
        currentFilter?.value.includes(m.id),
    );

    const valueStr = transformMultiselectToReadable(selectedMembers, {
        transform: c => `${c.first_name} ${c.last_name}`,
        cutoffLength: 1,
    });

    return (
        <LoadingOverlay isLoading={isPending} size={'sm'} delay={5000}>
            <ReadableFilterValue>{valueStr}</ReadableFilterValue>
        </LoadingOverlay>
    );
};

export const downstream: DownstreamFilterTransform<(string | number)[]> = identity;

export const upstream: UpstreamFilterTransform<(string | number)[]> = (
    filter: FilterValue<(string | number)[]>,
) =>
    injectEquals({
        value: filter.value,
    });
